<template>
  <Modal :open="open" @close="close">
    <div class="flex justify-center">
      <h2 v-if="isRenewalLead" class="text-2xl font-bold">Suivi du renouvellement</h2>
      <h2 v-else class="text-2xl font-bold">Suivi du lead</h2>
    </div>
    <div class="grid grid-cols-2 gap-6 my-10 text-sm">
      <div>
        <p class="block text-sm font-semibold leading-6 text-gray-900 sm:pt-1.5">Magasin</p>
        <FieldValue class="mt-2" :value="lead.storeSalesChannel" />
      </div>
      <div>
        <p class="block text-sm font-semibold leading-6 text-gray-900 sm:pt-1.5">Vendeur</p>
        <template v-if="canEditSeller">
          <div v-if="isLoadingSellers" class="mt-2 text-sm text-gray-500">Chargement des vendeurs...</div>
          <Selector
            v-else
            id="sellerId"
            name="sellerId"
            class="mt-2"
            :options="sellerOptions"
            v-model="formData.sellerId"
            defaultOptionText="Sélectionnez un vendeur"
          />
        </template>
        <FieldValue v-else class="mt-2" :value="lead.seller" />
      </div>
      <div>
        <p class="block text-sm font-semibold leading-6 text-gray-900 sm:pt-1.5">Formule</p>
        <FieldValue class="mt-2" :value="lead.formula" />
      </div>
      <div>
        <p class="block text-sm font-semibold leading-6 text-gray-900 sm:pt-1.5">Nom du client</p>
        <FieldValue class="mt-2" :value="lead.name" />
      </div>
      <div>
        <p class="block text-sm font-semibold leading-6 text-gray-900 sm:pt-1.5">Email du client</p>
        <FieldValue class="mt-2" :value="lead.email" />
      </div>
      <div>
        <p class="block text-sm font-semibold leading-6 text-gray-900 sm:pt-1.5">Téléphone du client</p>
        <FieldValue class="mt-2" :value="lead.phone" />
      </div>
      <template v-if="productRequiredForLead">
        <div>
          <p class="block text-sm font-semibold leading-6 text-gray-900 sm:pt-1.5">Nom du produit</p>
          <FieldValue class="mt-2" :value="lead.productName" />
        </div>
        <div>
          <p class="block text-sm font-semibold leading-6 text-gray-900 sm:pt-1.5">Prix du produit</p>
          <FieldValue class="mt-2" :value="lead.productPrice" />
        </div>
      </template>
      <div>
        <p class="block text-sm font-semibold leading-6 text-gray-900 sm:pt-1.5">Lien de souscription</p>
        <CopyToClipboard class="mt-2" :textToCopy="lead.subscriptionLink">Copier</CopyToClipboard>
      </div>
      <div>
        <p class="block text-sm font-semibold leading-6 text-gray-900 sm:pt-1.5">Date d'expiration</p>
        <FieldValue class="mt-2" :value="lead.expirationDate" />
      </div>
      <div v-if="isLiveLead">
        <p class="block text-sm font-semibold leading-6 text-gray-900 sm:pt-1.5">Promotion</p>
        <DiscountButton class="mt-2" :discounts="discounts" :lead="lead" @discountUpdated="discountUpdated" />
      </div>
      <FormField
        v-if="isLiveLead"
        id="objections"
        label="Objections"
        name="objections"
        placeholder="Objections"
        v-model="formData.objections"
      />
      <FormField
        v-if="isLiveLead"
        id="comments"
        label="Commentaires"
        name="comments"
        placeholder="Commentaires"
        v-model="formData.comments"
      />
      <FormField
        v-if="isLiveLead"
        id="callbackDate"
        label="Date du prochain appel"
        name="callbackDate"
        type="date"
        v-model="formData.callbackDate"
      />
    </div>
    <div v-if="isLiveLead" class="flex justify-between">
      <GenericButton @click="disableLead">Désactiver le lead</GenericButton>
      <GenericButton @click="updateLead">Valider</GenericButton>
    </div>
    <div v-else-if="canEditSeller" class="flex justify-end">
      <GenericButton @click="updateLead">Valider</GenericButton>
    </div>
  </Modal>
</template>

<script>
import { Modal, CopyToClipboard, GenericButton, FormField, Selector } from "@estaly/ui";
import DiscountButton from "@/views/leads/discount/DiscountButton";
import FieldValue from "@/components/utils/FieldValue.vue";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  components: {
    Modal,
    CopyToClipboard,
    DiscountButton,
    FormField,
    GenericButton,
    FieldValue,
    Selector,
  },

  props: ["open", "lead", "discounts"],

  data() {
    return {
      isLoadingSellers: false,
      formData: {
        objections: null,
        comments: null,
        callbackDate: null,
        discountId: null,
        sellerId: null,
        status: null,
      },
    };
  },

  computed: {
    ...mapGetters("leads", ["isLive", "productRequired", "isRenewal", "isWaitingForConfirmation"]),
    ...mapState("sellers", { sellers: (state) => state.index }),
    isLiveLead() {
      return this.isLive(this.lead);
    },
    productRequiredForLead() {
      return this.productRequired(this.lead);
    },
    isRenewalLead() {
      return this.isRenewal(this.lead);
    },
    isWaitingForConfirmationLead() {
      return this.isWaitingForConfirmation(this.lead);
    },
    canEditSeller() {
      return this.isLiveLead || this.isWaitingForConfirmationLead;
    },
    sellerOptions() {
      return this.sellers.map((seller) => ({
        value: seller.id,
        label: `${seller.firstName} ${seller.lastName}`,
      }));
    },
  },

  watch: {
    lead: {
      immediate: true,
      async handler(newLead) {
        if (newLead) {
          this.formData.comments = newLead.comments || null;
          this.formData.objections = newLead.objections || null;
          this.formData.callbackDate = newLead.callbackDate || null;
          this.formData.status = newLead.status;
          this.formData.discountId = newLead.discountId || null;
          this.formData.sellerId = newLead.sellerId || null;
          if (this.canEditSeller) {
            await this.fetchSellers();
          }
        }
      },
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("leads", ["update"]),
    ...mapActions("sellers", { fetchSellersIndex: "fetchIndex" }),

    async fetchSellers() {
      if (!this.lead?.storeId) return;
      this.isLoadingSellers = true;
      try {
        await this.fetchSellersIndex({ storeId: this.lead.storeId, perPage: 2000 });
      } finally {
        this.isLoadingSellers = false;
      }
    },
    discountUpdated(discountId) {
      this.formData.discountId = discountId || null;
    },
    disableLead() {
      this.formData.status = "disabled";
      this.updateLead();
    },
    async updateLead() {
      const { success } = await this.update({ leadId: this.lead.id, data: this.formData });
      if (!success) return;

      await this.notify({
        category: "simple",
        type: "success",
        title: "Le lead a été mis à jour avec succès.",
      });
      this.$emit("close");
      this.$emit("getLeads");
    },
  },
};
</script>
